import { Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { formatTimeAgo } from '@/lib/utils';
import { type Collection } from '@/features/collections/types/collection.types';

const CollectionCard = ({ collection }: { collection: Collection }) => {
  const navigate = useNavigate();

  const { letter, hue } = getCollectionCardStyle(collection.title);
  const isArchived = !!collection.archivedAt;

  return (
    <Card
      onClick={() => {
        navigate(`/shared/${collection.id}`);
      }}
      className="flex items-center justify-between rounded-lg hover:shadow-md cursor-pointer transition-all bg-background dark:bg-secondary my-4 p-3 pr-4"
    >
      <div className="flex items-start gap-3 w-full">
        <div
          className="mt-1 w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-md text-lg font-bold"
          style={{
            backgroundColor: `hsl(${hue}, 70%, 95%)`,
            color: `hsl(${hue}, 60%, 40%)`,
          }}
        >
          {letter}
        </div>

        <div className="flex flex-col overflow-hidden flex-grow min-w-0">
          <span className="text-foreground text-lg font-medium truncate">{collection.title}</span>

          {/* Description container with fixed height for 2 lines */}
          {collection.description && (
            <div className="max-h-[2.5rem] overflow-hidden">
              <p className="text-sm text-muted-foreground line-clamp-2">{collection.description}</p>
            </div>
          )}

          <span className="text-xs text-muted-foreground mt-1">
            {isArchived
              ? `Archived ${formatTimeAgo(collection.archivedAt)}`
              : `Created ${formatTimeAgo(collection.createdAt)}`}
          </span>
        </div>

        <div className="flex items-center gap-2 shrink-0 pl-2">
          {collection.isPublic && <Globe size={18} className="text-muted-foreground" />}
          {isArchived && <Badge className="bg-red-600 text-white text-[10px] h-5">Archived</Badge>}
        </div>
      </div>
    </Card>
  );
};

export default CollectionCard;

function getCollectionCardStyle(title: string) {
  const alphanumericMatch = title.match(/[a-zA-Z0-9]/);
  const firstLetter = alphanumericMatch ? alphanumericMatch[0].toUpperCase() : 'C';
  const charCode = firstLetter.charCodeAt(0);
  const hue = /[A-Z]/.test(firstLetter)
    ? ((charCode - 65) * 13) % 360
    : ((charCode - 48) * 36) % 360;

  return { letter: firstLetter, hue: String(hue) };
}
